import type { Breadcrumb } from "@sentry/react";

/**
 * @file
 * `maskAllText`/`maskAllInputs` don't reach URLs; Sentry stores them verbatim in breadcrumbs, errors (`request.url`,
 * `transaction`, message, stack frames), replay frames and `urls`, transactions and spans. Each channel has its
 * own hook: `beforeSend` sees errors only, `beforeSendSpan` every span, replays go through an event processor.
 *
 * The direct login `/klassenzimmer/:schoolClassId/login/:code` carries a class member's personal access
 * code — the link alone grants access to their account. Analytics scrubs it too (`useLogger`'s `page_path`,
 * `PageViewTracker`'s `page_name`).
 *
 * Deny list: login code, named parameters and emails. No UUID rule, unlike the website's: ids in this
 * app's URLs (tests, shares) make breadcrumbs useful and identify no one on their own.
 *
 * Kept close to the copies in the website and the vocabulary/grammar trainers.
 */

const FILTERED = "[Filtered]";

/** Entries must be lower-case: keys are compared lower-cased. */
const DENIED_PARAMS = new Set([
    "email",
    "firstname",
    "lastname",
    "code",
    "logincode",
    "session-id",
    "sessionid",
    "token",
    "jauthtoken",
    "access_token",
    "id_token",
    "refresh_token",
    "password",
]);

const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// Mirrors getClassMemberUrl (schoolClassHelpers), not imported: that module pulls in the app config.
// Case-insensitive: codes are normalized to upper case, but a typed URL may not be.
const LOGIN_CODE_PATTERN = /(\/klassenzimmer\/[^/?#]+\/login\/)[^/?#]+/gi;

const decodeKey = (key: string): string => {
    try {
        return decodeURIComponent(key).toLowerCase();
    } catch {
        return key.toLowerCase();
    }
};

const isDeniedPair = (pair: string): boolean => {
    const separator = pair.indexOf("=");
    return separator !== -1 && DENIED_PARAMS.has(decodeKey(pair.slice(0, separator)));
};

/** Split by hand rather than via URLSearchParams, which would re-encode untouched parameters. */
const scrubQueryString = (search: string): string =>
    search
        .split("&")
        .map((pair) => (isDeniedPair(pair) ? `${pair.split("=", 1)[0]}=${FILTERED}` : pair))
        .join("&");

const scrubPart = (part: string): string => {
    const queryStart = part.indexOf("?");
    if (queryStart === -1) return part;

    return part.slice(0, queryStart + 1) + scrubQueryString(part.slice(queryStart + 1));
};

/** Accepts absolute and relative URLs. Never throws: a failing scrubber would drop the whole event. */
export const scrubUrl = (url: string): string => {
    if (!url) return url;

    const hashStart = url.indexOf("#");
    const scrubbed =
        hashStart === -1
            ? scrubPart(url)
            : scrubPart(url.slice(0, hashStart)) + "#" + scrubPart(url.slice(hashStart + 1));

    return scrubbed.replace(LOGIN_CODE_PATTERN, `$1${FILTERED}`).replace(EMAIL_PATTERN, FILTERED);
};

/**
 * A replay holds the page address where no client hook reaches it — the replay's `urls` list, the rrweb
 * Meta frame, `href`s in DOM snapshots — so an address carrying an identifier must not be recorded at all.
 * Derived from `scrubUrl` so the two can't drift: anything it would scrub is unrecordable.
 */
export const isRecordableUrl = (url: string): boolean => scrubUrl(url) === url;

/** Navigation breadcrumbs use `from`/`to`, performance spans `previous`. */
const URL_FIELDS = ["from", "to", "previous", "url"];

const scrubUrlFields = (data: Record<string, unknown> | undefined) => {
    if (!data) return;

    for (const field of URL_FIELDS) {
        const value = data[field];
        if (typeof value === "string") data[field] = scrubUrl(value);
    }
};

const MAX_CONSOLE_ARG_DEPTH = 5;

/**
 * Copies: the breadcrumb holds the app's live objects. Non-plain objects (Errors, class instances) pass
 * through — none of our logs puts the page address there.
 */
const scrubConsoleArg = (value: unknown, depth = 0): unknown => {
    if (typeof value === "string") return scrubUrl(value);
    if (depth >= MAX_CONSOLE_ARG_DEPTH || value === null || typeof value !== "object") return value;
    if (Array.isArray(value)) return value.map((item) => scrubConsoleArg(item, depth + 1));
    if (Object.getPrototypeOf(value) !== Object.prototype) return value;

    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, scrubConsoleArg(item, depth + 1)]));
};

/**
 * Console output becomes a breadcrumb in prod too, so a logged `location.pathname` would carry the
 * login code. `message` is the joined text, `data.arguments` the raw arguments.
 */
const scrubConsole = (crumb: { message?: unknown; data?: Record<string, unknown> }) => {
    if (typeof crumb.message === "string") crumb.message = scrubUrl(crumb.message);
    if (crumb.data && Array.isArray(crumb.data.arguments)) crumb.data.arguments = scrubConsoleArg(crumb.data.arguments);
};

export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
    scrubUrlFields(breadcrumb.data);
    if (breadcrumb.category === "console") scrubConsole(breadcrumb);
    return breadcrumb;
};

export const scrubRecordingEvent = <T>(event: T): T => {
    const payload = (
        event as {
            data?: {
                payload?: {
                    category?: unknown;
                    message?: unknown;
                    description?: unknown;
                    data?: Record<string, unknown>;
                };
            };
        }
    )?.data?.payload;
    if (!payload) return event;

    if (typeof payload.description === "string") payload.description = scrubUrl(payload.description);
    scrubUrlFields(payload.data);
    if (payload.category === "console") scrubConsole(payload);

    return event;
};

type ScrubbableRequest = { url?: unknown; headers?: Record<string, unknown> };

/** HttpContext adds `url` (= `location.href` at send time) and a `Referer` header to every event, replays included. */
const scrubRequest = (request: ScrubbableRequest | undefined) => {
    if (!request) return;

    if (typeof request.url === "string") request.url = scrubUrl(request.url);
    if (typeof request.headers?.Referer === "string") request.headers.Referer = scrubUrl(request.headers.Referer);
};

type ScrubbableErrorEvent = {
    message?: string;
    request?: ScrubbableRequest;
    transaction?: string;
    exception?: {
        values?: Array<{ value?: string; stacktrace?: { frames?: Array<{ filename?: string; abs_path?: string }> } }>;
    };
};

/** Error messages and stack frames can carry the page address too, e.g. inline scripts or a message built from it. */
export const scrubErrorEvent = <T extends ScrubbableErrorEvent>(event: T): T => {
    if (event.message) event.message = scrubUrl(event.message);
    scrubRequest(event.request);
    if (event.transaction) event.transaction = scrubUrl(event.transaction);

    for (const exception of event.exception?.values ?? []) {
        if (exception.value) exception.value = scrubUrl(exception.value);

        for (const frame of exception.stacktrace?.frames ?? []) {
            if (frame.filename) frame.filename = scrubUrl(frame.filename);
            if (frame.abs_path) frame.abs_path = scrubUrl(frame.abs_path);
        }
    }

    return event;
};

/**
 * Event processor for `replay_event`s, which `beforeSend` never sees. Scrubs their `urls` list and `request` at
 * send time, closing the gap before `SentryReplayGuard`'s effect stops the recording; the Meta frame and DOM
 * `href`s stay out of reach, which is why the guard is still needed.
 */
export const scrubReplayEvent = <T extends { type?: string }>(event: T): T => {
    const replayEvent = event as T & { urls?: unknown; request?: ScrubbableRequest };
    if (replayEvent.type !== "replay_event") return event;

    if (Array.isArray(replayEvent.urls)) {
        replayEvent.urls = replayEvent.urls.map((url) => (typeof url === "string" ? scrubUrl(url) : url));
    }
    scrubRequest(replayEvent.request);

    return event;
};

/**
 * Span data keys vary by integration (`http.url`, `url`, …); a string under a "url" key is a URL in every case seen.
 * `transaction` is where standalone spans (INP) keep the page address; fetch/XHR spans split the query and
 * fragment off into `http.query`/`http.fragment`.
 */
const URL_PART_KEYS = new Set(["http.route", "http.query", "http.fragment", "transaction"]);
const isUrlDataKey = (key: string): boolean => key.toLowerCase().includes("url") || URL_PART_KEYS.has(key);

type ScrubbableSpan = { description?: unknown; data?: Record<string, unknown> };

const scrubSpanFields = (span: ScrubbableSpan) => {
    if (typeof span.description === "string") span.description = scrubUrl(span.description);
    if (!span.data) return;

    for (const [key, value] of Object.entries(span.data)) {
        if (typeof value === "string" && isUrlDataKey(key)) span.data[key] = scrubUrl(value);
    }
};

/** For `beforeSendSpan`: runs for every span — a transaction's root and children, and standalone spans (INP). */
export const scrubSpan = <T>(span: T): T => {
    if (span) scrubSpanFields(span as ScrubbableSpan);
    return span;
};

/** For `beforeSendTransaction`: the page address on the event itself. Its spans are `scrubSpan`'s job. */
export const scrubTransaction = <T>(event: T): T => {
    const transaction = event as { transaction?: unknown; request?: ScrubbableRequest };
    if (!transaction) return event;

    if (typeof transaction.transaction === "string") transaction.transaction = scrubUrl(transaction.transaction);
    scrubRequest(transaction.request);

    return event;
};
